import 'pdfjs-dist/web/pdf_viewer.css';
import debounce from 'lodash.debounce';
import type { PDFDocumentProxy, PDFLinkService, PDFViewer } from 'pdfjs-dist/es5/web/pdf_viewer';
import React, { useEffect, useLayoutEffect, useRef } from 'react';

import styles from './styles.css';
import { captureException } from '../../../Utils/sentry';

const PDF_MAX_WIDTH = 793;

enum LINK_TARGET {
  NONE = 0,
  SELF = 1,
  BLANK = 2,
  PARENT = 3,
  TOP = 4,
}

interface PdfProps {
  pdf?: PDFDocumentProxy;
  children?: React.ReactNode;
  onPdfRender?: () => void;
}

const Pdf: React.FC<PdfProps> = ({ pdf, onPdfRender, children }) => {
  const ref = useRef<HTMLDivElement>(null);
  const initializedPdfViewer = useRef<PDFViewer | null>(null);
  const initializedLinkService = useRef<PDFLinkService | null>(null);

  useEffect(() => {
    const handleResize = debounce(() => {
      if (!initializedPdfViewer.current || !ref.current) {
        return;
      }

      initializedPdfViewer.current.currentScaleValue = ref.current.clientWidth > PDF_MAX_WIDTH ? 'page-actual' : 'auto';
    }, 100);

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useLayoutEffect(() => {
    import(/* webpackMode: "eager" */ 'pdfjs-dist/es5/web/pdf_viewer')
      .then(({ EventBus, PDFLinkService, PDFViewer }) => {
        const eventBus = new EventBus();
        const linkService = new PDFLinkService({
          eventBus,
          externalLinkTarget: LINK_TARGET.BLANK,
        });

        const pdfViewer = new PDFViewer({
          container: ref.current!,
          eventBus,
          linkService,
        });

        initializedPdfViewer.current = pdfViewer;
        initializedLinkService.current = linkService;

        const onInit = () => {
          pdfViewer.currentScaleValue = ref.current!.clientWidth > PDF_MAX_WIDTH ? 'page-actual' : 'auto';
          onPdfRender && onPdfRender();
        };

        eventBus.on('pagesinit', onInit);

        if (pdf) {
          initializedPdfViewer.current.setDocument(pdf);
          initializedLinkService.current.setDocument(pdf, null);
        }
      })
      .catch(captureException);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useLayoutEffect(() => {
    if (pdf && initializedLinkService.current && initializedPdfViewer.current) {
      initializedPdfViewer.current.setDocument(pdf ?? null);
      initializedLinkService.current.setDocument(pdf ?? null, null);
    }

    return () => {
      initializedPdfViewer.current?.setDocument(null);
      initializedLinkService.current?.setDocument(null, null);
    };
  }, [pdf]);

  return (
    <>
      <div ref={ref} className={styles['pdf-document']}>
        <div className={styles['viewer']} />
      </div>
      {children}
    </>
  );
};

export default Pdf;
